import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Brain, Settings, Globe, Cloud, ArrowRight, Sparkles, LineChart, Smartphone, Code, Bot, Cog, CloudCog } from "lucide-react";
import Card from "../../../components/ui/Card";
import Button from "../../../components/ui/Button";
import SectionBadge from "../../../components/ui/SectionBadge";

const ServiceCards = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const services = [
    {
      id: 1,
      title: "AI Automation",
      description: "Automate repetitive tasks and workflows intelligently.",
      icon: "Bot",
      iconColor: "from-blue-600 to-cyan-500",
      link: "/services#ai-automation"
    },
    {
      id: 2,
      title: "Custom Internal Tools",
      description: "Tailored applications like CRM, HRMS, inventory dashboards, and more.",
      icon: "Cog",
      iconColor: "from-green-600 to-emerald-500",
      link: "/services#custom-tools"
    },
    {
      id: 3,
      title: "Web Development",
      description: "Modern, responsive, and fast web applications using React, Node, and cloud technologies.",
      icon: "Code",
      iconColor: "from-purple-600 to-pink-500",
      link: "/services#web-development"
    },
    {
      id: 4,
      title: "Cloud Integration",
      description: "Seamlessly connect platforms, APIs, and data for optimized operations.",
      icon: "CloudCog",
      iconColor: "from-orange-600 to-amber-500",
      link: "/services#cloud-integration"
    }
  ];

  return (
    <section ref={containerRef} className="relative py-16 md:py-20 3xl:py-32 overflow-hidden bg-slate-50 border-t border-gray-200">
      {/* Abstract Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-purple-400/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto relative z-10 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
        <div className="text-center max-w-3xl 3xl:max-w-4xl 4xl:max-w-5xl mx-auto mb-16 3xl:mb-24">
          <SectionBadge
            icon={() => (
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 0.6, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles className="3xl:w-5 3xl:h-5 4xl:w-6 4xl:h-6 5xl:w-7 5xl:h-7 6xl:w-8 6xl:h-8" />
              </motion.span>
            )}
            text="Our Services"
          />

          <h2 className="text-4xl md:text-5xl 3xl:text-6xl font-bold mb-6 3xl:mb-8 bg-gradient-to-r from-slate-900 via-blue-800 to-purple-900 bg-clip-text text-transparent leading-normal py-1">
            Comprehensive IT Solutions
          </h2>
          <p className="text-xl 3xl:text-2xl 4xl:text-3xl 5xl:text-4xl 6xl:text-5xl text-slate-600 leading-relaxed max-w-3xl 3xl:max-w-4xl 4xl:max-w-5xl mx-auto">
            We deliver scalable, secure, and customized IT solutions — helping your business grow in the digital world.
          </p>
        </div>

        <div className="w-full mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 md:gap-8 3xl:gap-12">
            {services.map((service, index) => (
              <Card
                key={service.id}
                variant="service"
                title={service.title}
                description={service.description}
                icon={service.icon}
                iconColor={service.iconColor}
                delay={index * 0.1}
                actions={
                  <Link to={service.link} className="flex items-center gap-2 text-blue-600 text-sm sm:text-base font-semibold group-hover:text-blue-700 transition-colors 3xl:text-lg">
                    <span>Learn More</span>
                    <ArrowRight className="w-4 h-4 3xl:w-5 3xl:h-5 transform group-hover:translate-x-1 transition-transform" />
                  </Link>
                }
              />
            ))}
          </div>
        </div>

        <motion.div
          className="text-center mt-12 md:mt-16 3xl:mt-24"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Link to="/services">
            <Button size="lg" icon="ExternalLink" iconPosition="right" className="3xl:px-8 3xl:py-4 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl">
              View All Services
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceCards;
